import React from "react";
import { get, isNil } from "lodash";
import { Grid, IconButton, Paper } from "@mui/material";
import HighlightOff from "@mui/icons-material/HighlightOff";
import { styled } from "@mui/material/styles";
import AutocompleteFreeform from "../../../common/js/components/AutocompleteFreeform";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import DocumentDrop from "../documents/DocumentDropContainer";
import FormTitle from "../../../common/js/components/FormTitle";
import { Search } from "../../../common/js/icons";
import { us_states_options } from "../utils/constants";

const StyledPaper = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledClearButton = styled(IconButton)({
    "&:hover": {
        backgroundColor: "transparent",
        color: "black",
    },
    padding: "0px",
});

const StyledHeader = styled(Grid)({
    paddingBottom: "0px",
    height: "6em",
});

const EditTransaction = (props) => {
    const { errors, transaction, autocompleteResults, searchProperty, disableInputs, handleInput, searchCompany } =
        props;

    const selectProperty = (property) => {
        handleInput(`transaction.property`, property);
    };

    const clearProperty = () => {
        handleInput(`transaction.property`, {});
    };

    const selectBorrowerCompany = (company) => {
        handleInput(`transaction.borrowerCompany`, company);
    };

    const clearBorrowerCompany = () => {
        handleInput(`transaction.borrowerCompany`, {});
    };

    const selectBorrowerParentCompany = (company) => {
        handleInput(`transaction.borrowerParentCompany`, company);
    };

    const clearBorrowerParentCompany = () => {
        handleInput(`transaction.borrowerParentCompany`, {});
    };

    const handlePropertyFreeform = (value) => {
        handleInput("transaction.property", { propertyAttributes: { propertyName: value } });
    };

    const handleBorrowerCompanyFreeform = (value) => {
        handleInput("transaction.borrowerCompany", { legalName: value });
    };
    const handleBorrowerParentCompanyFreeform = (value) => {
        handleInput("transaction.borrowerParentCompany", { displayName: value, legalName: value });
    };

    return (
        <React.Fragment>
            <StyledPaper>
                <StyledHeader item xs={12}>
                    <Grid container alignItems="center" spacing={8}>
                        <Grid item xs>
                            <FormTitle title="General Information" />
                            <hr style={{ margin: "0px -20px 25px", borderTop: "1px solid #eeeeee" }} />
                        </Grid>
                    </Grid>
                </StyledHeader>
                <Grid container spacing={40}>
                    <Grid item xs={6}>
                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <BSInput
                                    id="transactionName"
                                    label="Transaction Name"
                                    value={get(transaction, `transactionName`, "")}
                                    error={get(errors, `transactionName`, null)}
                                    onChange={(e) => handleInput("transaction.transactionName", e.target.value)}
                                    placeholder="Enter transaction name"
                                    disabled={disableInputs === true}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={6}>
                                <BSInput
                                    id="firstName"
                                    label="Client First Name"
                                    value={get(transaction, `client.person.firstName`, "")}
                                    error={get(errors, `client.person.firstName`, null)}
                                    onChange={(e) => handleInput("transaction.client.person.firstName", e.target.value)}
                                    placeholder="Enter first name"
                                    disabled={disableInputs === true}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <BSInput
                                    id="lastName"
                                    label="Client Last Name"
                                    value={get(transaction, `client.person.lastName`, "")}
                                    error={get(errors, `client.person.lastName`, null)}
                                    onChange={(e) => handleInput("transaction.client.person.lastName", e.target.value)}
                                    placeholder="Enter last name"
                                    disabled={disableInputs === true}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={6}>
                                <BSInput
                                    id="phoneNumber"
                                    label="Client Phone"
                                    value={get(transaction, `client.phoneNumber.phoneNumber`, "")}
                                    error={get(errors, `client.phoneNumber.phoneNumber`, null)}
                                    onChange={(e) =>
                                        handleInput("transaction.client.phoneNumber.phoneNumber", e.target.value)
                                    }
                                    errorText="Invalid"
                                    placeholder="Enter transaction phone"
                                    disabled={disableInputs === true}
                                    phone={true}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <BSInput
                                    id="emailAddress"
                                    label="Client Email"
                                    value={get(transaction, `client.emailAddress`, "")}
                                    error={get(errors, `client.emailAddress`, null)}
                                    onChange={(e) => handleInput("transaction.client.emailAddress", e.target.value)}
                                    placeholder="Enter transaction email"
                                    disabled={disableInputs === true}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <AutocompleteFreeform
                                    id="property"
                                    label="Property Name"
                                    error={get(errors, `property.propertyKey`, null)}
                                    initialInputValue={get(transaction, `property.propertyAttributes.propertyName`, "")}
                                    itemToString={(i) =>
                                        i == null ? "" : get(i, "propertyAttributes.propertyName", "")
                                    }
                                    onSelect={(property, b) => selectProperty(property)}
                                    freeformUpdate={(value) => handlePropertyFreeform(value)}
                                    placeholder="Search or create property"
                                    searchFunction={(input) => searchProperty(input)}
                                    startAdornment={<Search />}
                                    endAdornment={
                                        !isNil(get(transaction, `property.propertyKey`, null)) ? (
                                            <StyledClearButton onClick={clearProperty}>
                                                <HighlightOff style={{ fontSize: "16px" }} />
                                            </StyledClearButton>
                                        ) : null
                                    }
                                    suggestions={autocompleteResults}
                                    disabled={
                                        disableInputs === true || !isNil(get(transaction, `property.propertyKey`, null))
                                    }
                                    options={{
                                        key: "propertyKey",
                                        display: (suggestion) => {
                                            return get(suggestion, "propertyAttributes.propertyName", "");
                                        },
                                        displaySub: (suggestion) => {
                                            return get(suggestion, "primaryAddress.address1", "");
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <BSInput
                                    id="address1"
                                    label="Property Address"
                                    value={get(transaction, `property.primaryAddress.address1`, "")}
                                    error={get(errors, `property.primaryAddress.address1`, null)}
                                    onChange={(e) =>
                                        handleInput("transaction.property.primaryAddress.address1", e.target.value)
                                    }
                                    errorText="Required"
                                    placeholder="Enter address"
                                    disabled={
                                        disableInputs === true || !isNil(get(transaction, `property.propertyKey`, null))
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={4}>
                                <BSInput
                                    id="city"
                                    label="City"
                                    value={get(transaction, `property.primaryAddress.city.city`, "")}
                                    error={get(errors, `property.primaryAddress.city.city`, null)}
                                    onChange={(e) =>
                                        handleInput("transaction.property.primaryAddress.city.city", e.target.value)
                                    }
                                    errorText="Required"
                                    placeholder="Enter city"
                                    disabled={
                                        disableInputs === true || !isNil(get(transaction, `property.propertyKey`, null))
                                    }
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <BSSelect
                                    id="state"
                                    label="State"
                                    value={get(transaction, `property.primaryAddress.city.state.stateCode`, "")}
                                    error={get(errors, `property.primaryAddress.city.state.stateCode`, null)}
                                    errorText="Required"
                                    onChange={(e) =>
                                        handleInput(
                                            "transaction.property.primaryAddress.city.state.stateCode",
                                            e.target.value
                                        )
                                    }
                                    options={us_states_options}
                                    disabled={
                                        disableInputs === true || !isNil(get(transaction, `property.propertyKey`, null))
                                    }
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <BSInput
                                    id="zipCode"
                                    label="Zip Code"
                                    value={get(transaction, `property.primaryAddress.zipCode.zipCode`, "")}
                                    error={get(errors, `property.primaryAddress.zipCode.zipCode`, null)}
                                    onChange={(e) =>
                                        handleInput(
                                            "transaction.property.primaryAddress.zipCode.zipCode",
                                            e.target.value
                                        )
                                    }
                                    errorText="Invalid"
                                    placeholder="Enter zip code"
                                    disabled={
                                        disableInputs === true || !isNil(get(transaction, `property.propertyKey`, null))
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={6}>
                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <AutocompleteFreeform
                                    id="borrowerCompanyName"
                                    label="Borrower LLC Name"
                                    error={get(errors, `borrowerCompany.legalName`, null)}
                                    initialInputValue={get(transaction, `borrowerCompany.legalName`, "")}
                                    itemToString={(i) => (i == null ? "" : get(i, "legalName", ""))}
                                    onSelect={(company) => selectBorrowerCompany(company)}
                                    freeformUpdate={(value) => handleBorrowerCompanyFreeform(value)}
                                    placeholder="Search or create company"
                                    searchFunction={(input) => searchCompany(input)}
                                    startAdornment={<Search />}
                                    endAdornment={
                                        !isNil(get(transaction, `borrowerCompany.companyKey`, null)) ? (
                                            <StyledClearButton onClick={clearBorrowerCompany}>
                                                <HighlightOff style={{ fontSize: "16px" }} />
                                            </StyledClearButton>
                                        ) : null
                                    }
                                    suggestions={autocompleteResults}
                                    disabled={
                                        disableInputs === true ||
                                        !isNil(get(transaction, `borrowerCompany.companyKey`, null))
                                    }
                                    options={{
                                        key: "companyKey",
                                        display: (suggestion) => {
                                            return get(suggestion, "legalName", "");
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <BSInput
                                    id="borrowerCompanyName"
                                    label="Borrower Common Name"
                                    value={get(transaction, `borrowerCompany.displayName`, "")}
                                    error={get(errors, `borrowerCompany.displayName`, null)}
                                    onChange={(e) =>
                                        handleInput("transaction.borrowerCompany.displayName", e.target.value)
                                    }
                                    placeholder="Enter company common name"
                                    disabled={
                                        disableInputs === true ||
                                        !isNil(get(transaction, `borrowerCompany.companyKey`, null))
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <AutocompleteFreeform
                                    id="borrowerParentCompany"
                                    label="Borrower Parent Company"
                                    error={get(errors, `borrowerParentCompany.displayName`, null)}
                                    initialInputValue={get(transaction, `borrowerParentCompany.displayName`, "")}
                                    itemToString={(i) => (i == null ? "" : get(i, "displayName", ""))}
                                    onSelect={(company) => selectBorrowerParentCompany(company)}
                                    freeformUpdate={(value) => handleBorrowerParentCompanyFreeform(value)}
                                    placeholder="Search or create company"
                                    searchFunction={(input) => searchCompany(input)}
                                    startAdornment={<Search />}
                                    endAdornment={
                                        !isNil(get(transaction, `borrowerParentCompany.companyKey`, null)) ? (
                                            <StyledClearButton onClick={clearBorrowerParentCompany}>
                                                <HighlightOff style={{ fontSize: "16px" }} />
                                            </StyledClearButton>
                                        ) : null
                                    }
                                    suggestions={autocompleteResults}
                                    isOpen={autocompleteResults.length > 0}
                                    disabled={
                                        disableInputs === true ||
                                        !isNil(get(transaction, `borrowerParentCompany.companyKey`, null))
                                    }
                                    options={{
                                        key: "companyKey",
                                        display: (suggestion) => {
                                            return get(suggestion, "displayName", "");
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={6}>
                                <BSInput
                                    id="purchasePrice"
                                    label="Purchase Price"
                                    value={get(transaction, `purchasePrice`, "")}
                                    error={get(errors, `purchasePrice`, null)}
                                    onChange={(e) => handleInput("transaction.purchasePrice", e.target.value)}
                                    placeholder="Enter amount"
                                    disabled={disableInputs === true}
                                    startAdornment="$"
                                    numeric
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <BSInput
                                    id="refiValue"
                                    label="Value of REFI"
                                    value={get(transaction, `refiValue`, "")}
                                    error={get(errors, `refiValue`, null)}
                                    onChange={(e) => handleInput("transaction.refiValue", e.target.value)}
                                    placeholder="Enter amount"
                                    disabled={disableInputs === true}
                                    startAdornment="$"
                                    numeric
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={8}>
                            <Grid item xs={6}>
                                <BSInput
                                    id="loanAmount"
                                    label="Loan Amount"
                                    value={get(transaction, `loanAmount`, "")}
                                    error={get(errors, `loanAmount`, null)}
                                    onChange={(e) => handleInput("transaction.loanAmount", e.target.value)}
                                    placeholder="Enter amount"
                                    disabled={disableInputs === true}
                                    startAdornment="$"
                                    numeric
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <BSInput
                                    id="refiBalance"
                                    label="Balance if REFI"
                                    value={get(transaction, `refiBalance`, "")}
                                    error={get(errors, `refiBalance`, null)}
                                    onChange={(e) => handleInput("transaction.refiBalance", e.target.value)}
                                    placeholder="Enter amount"
                                    disabled={disableInputs === true}
                                    startAdornment="$"
                                    numeric
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </StyledPaper>

            <DocumentDrop
                entityKey={get(transaction, "transactionKey")}
                type="transaction"
                documentTypes="transaction"
                parentType={"TRANSACTION"}
                formDisabled={disableInputs}
                errors={get(errors, "documents")}
                alternateTitle="Data Room"
            />
        </React.Fragment>
    );
};

export default EditTransaction;
